import React, { useEffect, useMemo, useState } from 'react';
import './SplitPayment.css';

const mesAtual = () => {
  const agora = new Date();
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;
};

const moeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const modalidades = { cpfl: 'CPFL', pix: 'Pix', asaas: 'Asaas' };

const tipoResponsavelLabel = (tipo) =>
  tipo === 'externo' ? 'Externo' : 'Interno';

const classeStatusCobranca = (status) =>
  String(status || 'PENDENTE')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export default function SplitPayment() {
  const [mes, setMes] = useState(mesAtual());
  const [responsavelKey, setResponsavelKey] = useState('');
  const [clientes, setClientes] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let cancelado = false;

    const carregar = async () => {
      setCarregando(true);
      setErro('');

      try {
        const token = localStorage.getItem('token');
        const resposta = await fetch(
          `/api/clients/repasse/split-payment?mes=${encodeURIComponent(mes)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const dados = await resposta.json();

        if (!resposta.ok) {
          throw new Error(dados.error || 'Não foi possível carregar a consulta.');
        }

        if (!cancelado) {
          setClientes(dados.clients || []);
          setResponsaveis(dados.responsibles || []);
        }
      } catch (e) {
        if (!cancelado) setErro(e.message);
      } finally {
        if (!cancelado) setCarregando(false);
      }
    };

    carregar();
    return () => { cancelado = true; };
  }, [mes]);

  const filtrados = useMemo(() => clientes.filter((cliente) =>
    !responsavelKey ||
    (cliente.responsaveis || []).some(
      (responsavel) => responsavel.key === responsavelKey
    )
  ), [clientes, responsavelKey]);

  const formatarData = (valor) => {
    if (!valor) return '—';
    const data = new Date(valor);
    return Number.isNaN(data.getTime())
      ? '—'
      : data.toLocaleDateString('pt-BR');
  };

  const formatarValor = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '—';
    const numero = Number(valor);
    return Number.isFinite(numero) ? moeda.format(numero) : '—';
  };

  return (
    <main className="split-payment-page">
      <header className="split-payment-header">
        <div>
          <span className="split-payment-eyebrow">REPASSE PARCEIRO</span>
          <h1>Split Payment</h1>
          <p>Consulta de clientes e responsáveis para conferência do gestor.</p>
        </div>
        <strong className="split-payment-count">{filtrados.length} clientes</strong>
      </header>

      <section className="split-payment-filters" aria-label="Filtros da consulta">
        <label>
          Mês do cadastro
          <input
            type="month"
            value={mes}
            onChange={(e) => {
              setMes(e.target.value);
              setResponsavelKey('');
            }}
          />
        </label>

        <label>
          Atendente/Vendedor
          <select
            value={responsavelKey}
            onChange={(e) => setResponsavelKey(e.target.value)}
          >
            <option value="">Todos os responsáveis</option>
            {responsaveis.map((responsavel) => (
              <option key={responsavel.key} value={responsavel.key}>
                {responsavel.name} — {tipoResponsavelLabel(responsavel.tipo)}
              </option>
            ))}
          </select>
        </label>
      </section>

      {carregando && (
        <div className="split-payment-feedback">Carregando clientes...</div>
      )}

      {!carregando && erro && (
        <div className="split-payment-feedback split-payment-error">{erro}</div>
      )}

      {!carregando && !erro && (
        <div className="split-payment-table-wrap">
          <table className="split-payment-table">
            <thead>
              <tr>
                <th>Cadastro</th>
                <th>Cliente</th>
                <th>Atendente/Vendedor</th>
                <th>Plano</th>
                <th>Valor do plano</th>
                <th>Cidade</th>
                <th>Modalidade</th>
                <th>Status da cobrança</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((cliente) => (
                <tr key={cliente.id}>
                  <td>{formatarData(cliente.created_date)}</td>
                  <td className="split-payment-name">{cliente.full_name || '—'}</td>
                  <td>
                    {cliente.responsaveis?.length ? (
                      <div className="split-payment-responsibles">
                        {cliente.responsaveis.map((responsavel) => (
                          <span
                            key={responsavel.key}
                            className={`split-payment-responsible ${responsavel.tipo}`}
                          >
                            {responsavel.name}
                            <small>{tipoResponsavelLabel(responsavel.tipo)}</small>
                          </span>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td>{cliente.plan_name || '—'}</td>
                  <td className="split-payment-value">
                    {formatarValor(cliente.plan_value)}
                  </td>
                  <td>{cliente.city || '—'}</td>
                  <td>
                    <span className={`split-payment-badge ${cliente.payment_type || ''}`}>
                      {modalidades[cliente.payment_type] || cliente.payment_type || '—'}
                    </span>
                  </td>
                  <td>
                    <span className={`split-payment-charge-status ${classeStatusCobranca(cliente.cobranca_status)}`}>
                      {String(cliente.cobranca_status || 'PENDENTE').toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}

              {filtrados.length === 0 && (
                <tr>
                  <td colSpan="8" className="split-payment-empty">
                    Nenhum cliente encontrado para esses filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="split-payment-note">
        Esta tela é somente para consulta e conferência. Não executa pagamentos.
      </p>
    </main>
  );
}
